import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation
} from 'react-router-dom';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Game from './components/Game';
import Leaderboard from './components/Leaderboard';
import HallOfFame from './components/HallOfFame';
import AdminPanel from './components/AdminPanel';
import Profile from './components/Profile';
import Rules from './components/Rules';
import GameHub from './components/GameHub';
import DozenGame from './components/DozenGame';
import DozenAdmin from './components/DozenAdmin';
import DozenRules from './components/DozenRules';
import DozenHall from './components/DozenHall';
import { AuthProvider, useAuth } from './context/AuthContext';
import {
  WORD_APP_PATH,
  DOZEN_APP_PATH,
  DOZEN_API_URL,
  HUB_PATH,
  ADMIN_HUB_PATH,
  PROFILE_PATH
} from './config';
import { DOZEN_APP } from './apps';
import { adminEntryPath } from './utils/adminNav';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading fullscreen">Загрузка...</div>;
  }

  return user ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading fullscreen">Загрузка...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!user.isAdmin) {
    return <Navigate to={HUB_PATH} />;
  }

  return children;
}

function ProfileLegacyRedirect() {
  const { username } = useParams();
  return <Navigate to={`${PROFILE_PATH}/${username}/word`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Shared auth — reusable by future apps on this site */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Word of the Day app */}
      <Route
        path={`${WORD_APP_PATH}/game`}
        element={
          <ProtectedRoute>
            <Game />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${WORD_APP_PATH}/leaderboard`}
        element={
          <ProtectedRoute>
            <Leaderboard key="word" />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${WORD_APP_PATH}/hall`}
        element={
          <ProtectedRoute>
            <HallOfFame />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${WORD_APP_PATH}/admin`}
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />
      <Route
        path={`${WORD_APP_PATH}/profile/:username`}
        element={<ProfileLegacyRedirect />}
      />
      {/* Public — readable before login */}
      <Route path={`${WORD_APP_PATH}/rules`} element={<Rules />} />
      <Route
        path={WORD_APP_PATH}
        element={<Navigate to={`${WORD_APP_PATH}/game`} replace />}
      />

      {/* Дюжина */}
      <Route
        path={`${DOZEN_APP_PATH}/game`}
        element={
          <ProtectedRoute>
            <DozenGame />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${DOZEN_APP_PATH}/leaderboard`}
        element={
          <ProtectedRoute>
            <Leaderboard
              key="dozen"
              apiUrl={DOZEN_API_URL}
              app={DOZEN_APP}
              wordsPerGame={12}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${DOZEN_APP_PATH}/hall`}
        element={
          <ProtectedRoute>
            <DozenHall />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${DOZEN_APP_PATH}/admin`}
        element={
          <AdminRoute>
            <DozenAdmin />
          </AdminRoute>
        }
      />
      <Route path={`${DOZEN_APP_PATH}/rules`} element={<DozenRules />} />
      <Route
        path={DOZEN_APP_PATH}
        element={<Navigate to={`${DOZEN_APP_PATH}/game`} replace />}
      />

      <Route
        path={ADMIN_HUB_PATH}
        element={
          <AdminRoute>
            <Navigate to={adminEntryPath()} replace />
          </AdminRoute>
        }
      />

      {/* Legacy redirects */}
      <Route path="/game" element={<Navigate to={`${WORD_APP_PATH}/game`} replace />} />
      <Route
        path="/leaderboard"
        element={<Navigate to={`${WORD_APP_PATH}/leaderboard`} replace />}
      />
      <Route
        path={`${PROFILE_PATH}/:username/:game`}
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route path={`${PROFILE_PATH}/:username`} element={<ProfileLegacyRedirect />} />
      <Route path="/profile/:username" element={<ProfileLegacyRedirect />} />

      {/* Site root — choose a game */}
      <Route
        path={HUB_PATH}
        element={
          <ProtectedRoute>
            <GameHub />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
